import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { FaArrowUp, FaArrowDown, FaEye, FaTrash, FaPlus } from "react-icons/fa";
import { getToken } from "../../api/auth";
import { getCities, addCity, deleteCity } from "../../api/routeOptimization";
import "./Cities.css";

const Cities = () => {
  const token = getToken();
  const [cities, setCities] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [citiesPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [pageInput, setPageInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [newCity, setNewCity] = useState({
    code: "",
    city1: "",
    countryregioncode: "",
    county: "",
    skillsid: 1,
    pickupordelivery: false,
    km: 0,
    timezone: null,
  });

  useEffect(() => {
    const fetchCities = async () => {
      setLoading(true);
      try {
        const response = await getCities();
        const citiesData = response.city;

        if (!citiesData || !Array.isArray(citiesData)) {
          setCities([]);
          return;
        }

        setCities(citiesData);
      } catch (err) {
        console.error("Failed to fetch cities:", err);
        setError("Failed to fetch cities. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchCities();
    }
  }, [token]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedCities = [...cities].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valA = a[sortConfig.key];
    const valB = b[sortConfig.key];

    if (typeof valA === "string" && typeof valB === "string") {
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredCities = sortedCities.filter((city) => {
    const matchesSearch =
      (city.code || "").toString().includes(searchTerm.toLowerCase()) ||
      (city.city1 || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (city.countryregioncode || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (city.county || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCountry =
      countryFilter === "All" || city.countryregioncode === countryFilter;

    return matchesSearch && matchesCountry;
  });

  const indexOfLastCity = currentPage * citiesPerPage;
  const indexOfFirstCity = indexOfLastCity - citiesPerPage;
  const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);
  const totalPages = Math.ceil(filteredCities.length / citiesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setPageInput(pageNumber.toString());
    }
  };

  const handlePageInput = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageSubmit = (e) => {
    if (e.key === "Enter") {
      const page = parseInt(pageInput);
      if (!isNaN(page) && page >= 1 && page <= totalPages) {
        paginate(page);
      } else {
        setPageInput(currentPage.toString());
      }
    }
  };

  const openAddModal = () => {
    setNewCity({
      code: "",
      city1: "",
      countryregioncode: "",
      county: "",
      skillsid: 1,
      pickupordelivery: false,
      km: 0,
      timezone: null,
    });
    setIsAddModalOpen(true);
    setError(null);
  };

  const openViewModal = (city) => {
    setSelectedCity(city);
    setIsViewModalOpen(true);
    setError(null);
  };

  const handleAddCity = async (e) => {
    e.preventDefault();
    try {
      await addCity(newCity);
      const response = await getCities();
      setCities(response.city);
      setIsAddModalOpen(false);
      setNewCity({
        code: "",
        city1: "",
        countryregioncode: "",
        county: "",
        skillsid: 1,
        pickupordelivery: false,
        km: 0,
        timezone: null,
      });
    } catch (err) {
      setError("Failed to add city. Please try again.");
    }
  };

  const handleDeleteCity = async (code) => {
    try {
      await deleteCity(code);
      const response = await getCities();
      setCities(response.city);
      setShowDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete city. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading cities...</p>
      </div>
    );
  }

  return (
    <div className="city-list-container">
      <div className="floating-bg floating-bg-1"></div>
      <div className="floating-bg floating-bg-2"></div>
      <div className="floating-bg floating-bg-3"></div>

      <div className="page-header">
        <h2 className="city-list-title">City List</h2>
        <p className="page-subtitle">Manage cities for route optimization</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by code, city, country, or county..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
        >
          <option value="All">All Countries</option>
          {[...new Set(cities.map((city) => city.countryregioncode))].map(
            (country) => (
              <option key={country} value={country}>
                {country}
              </option>
            )
          )}
        </select>
        <button className="glass-btn add-city-btn" onClick={openAddModal}>
          <FaPlus size={20} /> Add City
        </button>
      </div>

      <div className="city-table-container">
        <table className="city-table">
          <thead>
            <tr>
              {[
                { key: "code", label: "Code" },
                { key: "city1", label: "City" },
                { key: "countryregioncode", label: "Country" },
                { key: "county", label: "County" },
                { key: "pickupordelivery", label: "Pickup/Delivery" },
                { key: "skillsid", label: "Skills ID" },
                // { key: "km", label: "Distance (km)" },
              ].map(({ key, label }) => (
                <th
                  key={key}
                  className="glass-header"
                  onClick={() => handleSort(key)}
                >
                  {label}
                  {sortConfig.key === key && (
                    <span className="sort-icon">
                      {sortConfig.direction === "asc" ? (
                        <FaArrowUp />
                      ) : (
                        <FaArrowDown />
                      )}
                    </span>
                  )}
                </th>
              ))}
              <th className="glass-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCities.map((city, idx) => (
              <tr key={city.code} className={`row-animate row-animate-${idx}`}>
                <td>{city.code}</td>
                <td>{city.city1}</td>
                <td>{city.countryregioncode}</td>
                <td>{city.county}</td>
                <td>{city.pickupordelivery ? "Yes" : "No"}</td>
                <td>{city.skillsid}</td>
                {/* <td>{city.km}</td> */}
                <td>
                  <button
                    className="glass-btn btn-primary"
                    onClick={() => openViewModal(city)}
                  >
                    <FaEye size={16} /> View
                  </button>
                  <button
                    className="glass-btn btn-delete"
                    onClick={() => setShowDeleteConfirm(city.code)}
                  >
                    <FaTrash size={16} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="city-cards">
          {currentCities.map((city, idx) => (
            <div
              key={city.code}
              className={`city-card row-animate row-animate-${idx}`}
            >
              <div>
                <strong>Code:</strong> {city.code}
              </div>
              <div>
                <strong>City:</strong> {city.city1}
              </div>
              <div>
                <strong>Country:</strong> {city.countryregioncode}
              </div>
              <div>
                <strong>County:</strong> {city.county}
              </div>
              <div>
                <strong>Pickup/Delivery:</strong>{" "}
                {city.pickupordelivery ? "Yes" : "No"}
              </div>
              <div>
                <strong>Skils Id:</strong> {city.skillsid} km
              </div>
              <button
                className="glass-btn btn-primary"
                onClick={() => openViewModal(city)}
              >
                <FaEye size={16} /> View
              </button>
              <button
                className="glass-btn btn-delete"
                onClick={() => setShowDeleteConfirm(city.code)}
              >
                <FaTrash size={16} /> Delete
              </button>
            </div>
          ))}
        </div>

        {currentCities.length === 0 && (
          <div className="empty-state">
            <p>No cities found</p>
          </div>
        )}

        <div className="table-footer">
          <div className="pagination">
            <button
              className="glass-btn pagination-btn pagination-btn-xs"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <input
              type="text"
              value={pageInput}
              onChange={handlePageInput}
              onKeyDown={handlePageSubmit}
              className="pagination-input"
              placeholder="Page"
            />
            <button
              className="glass-btn pagination-btn pagination-btn-xs"
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
          <div className="row-count-container">
            <span className="row-count">
              {citiesPerPage} rows from {filteredCities.length}
            </span>
          </div>
        </div>
      </div>

      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Add City</h3>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleAddCity}>
              <div className="form-group">
                <label htmlFor="code">City Code</label>
                <input
                  type="text"
                  id="code"
                  value={newCity.code}
                  onChange={(e) =>
                    setNewCity({ ...newCity, code: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="city1">City Name</label>
                <input
                  type="text"
                  id="city1"
                  value={newCity.city1}
                  onChange={(e) =>
                    setNewCity({ ...newCity, city1: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="countryregioncode">Country Code</label>
                <input
                  type="text"
                  id="countryregioncode"
                  value={newCity.countryregioncode}
                  onChange={(e) =>
                    setNewCity({
                      ...newCity,
                      countryregioncode: e.target.value,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="county">County</label>
                <input
                  type="text"
                  id="county"
                  value={newCity.county}
                  onChange={(e) =>
                    setNewCity({ ...newCity, county: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="pickupordelivery">Pickup/Delivery</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="pickupordelivery"
                    checked={newCity.pickupordelivery}
                    onChange={(e) =>
                      setNewCity({
                        ...newCity,
                        pickupordelivery: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="pickupordelivery" className="toggle-label">
                    <span className="toggle-text">
                      {newCity.pickupordelivery ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="km">Distance (km)</label>
                <input
                  type="number"
                  id="km"
                  value={newCity.km}
                  onChange={(e) =>
                    setNewCity({ ...newCity, km: Number(e.target.value) })
                  }
                  min="0"
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="glass-btn cancel-btn"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="glass-btn submit-btn">
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isViewModalOpen && selectedCity && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>View City</h3>
            <div className="agent-detail-row">
              <span className="detail-label">Code:</span>
              <span className="detail-value">{selectedCity.code}</span>
            </div>
            <div className="agent-detail-row">
              <span className="detail-label">City:</span>
              <span className="detail-value">{selectedCity.city1}</span>
            </div>
            <div className="agent-detail-row">
              <span className="detail-label">Country:</span>
              <span className="detail-value">
                {selectedCity.countryregioncode}
              </span>
            </div>
            <div className="agent-detail-row">
              <span className="detail-label">County:</span>
              <span className="detail-value">{selectedCity.county}</span>
            </div>
            <div className="agent-detail-row">
              <span className="detail-label">Pickup/Delivery:</span>
              <span className="detail-value">
                {selectedCity.pickupordelivery ? "Yes" : "No"}
              </span>
            </div>
            <div className="agent-detail-row">
              <span className="detail-label">Distance (km):</span>
              <span className="detail-value">{selectedCity.km}</span>
            </div>
            <div className="agent-detail-row">
              <span className="detail-label">Skills ID:</span>
              <span className="detail-value">{selectedCity.skillsid}</span>
            </div>
            <div className="agent-detail-row">
              <span className="detail-label">Timezone:</span>
              <span className="detail-value">
                {selectedCity.timezone || "N/A"}
              </span>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="glass-btn cancel-btn"
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this city?</p>
            {error && <div className="error-message">{error}</div>}
            <div className="modal-actions">
              <button
                className="glass-btn cancel-btn"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="glass-btn delete-btn"
                onClick={() => handleDeleteCity(showDeleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cities;
